import React, { useState } from "react";
import {
  Box,
  Typography,
  Stack,
  Divider,
  Paper,
  Chip,
  Grid,
  Dialog,
  IconButton,
} from "@mui/material";
import {
  LocationOn,
  AccessTime,
  Phone,
  Info,
  EmojiEvents,
  Star,
  FitnessCenter,
  Close,
  Verified,
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { AnimatePresence, motion } from "framer-motion";

// === Swiper Imports ===
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Zoom } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/zoom";

import CommentsSection from "../../../../Components/CommentsSection"; // Adjust path if needed

export default function FullDetails({ gymData, gymId }) {
  const theme = useTheme();

  // Lightbox States
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [initialSlide, setInitialSlide] = useState(0);

  const getLocationString = () => {
    if (!gymData?.location) return "مشهد";
    const cityName = gymData.location.city?.name || "";
    const areaNames =
      gymData.location.areas?.map((area) => area?.name).join("، ") || "";
    return areaNames ? `${areaNames}، ${cityName}` : cityName;
  };

  const getFullAddress = () =>
    gymData?.location?.address || "آدرس ثبت نشده است";
  const getWorkingHours = () => gymData?.workingHours || "نامشخص";
  const getSportNames = () =>
    gymData?.sport?.length > 0 ? gymData.sport.map((s) => s?.name) : ["ورزشی"];
  const getGalleryImages = () => gymData?.gallery || [];
  const getAchievements = () => gymData?.achievements || [];
  const getAmenities = () =>
    gymData?.amenities?.length > 0
      ? gymData.amenities
      : ["سرویس بهداشتی", "رختکن"];

  const handleOpenLightbox = (index) => {
    setInitialSlide(index);
    setLightboxOpen(true);
  };

  const handleCloseLightbox = () => {
    setLightboxOpen(false);
  };

  // Reusable Section Card
  const SectionCard = ({ title, icon, children }) => (
    <Paper
      component="section"
      sx={{
        p: { xs: 2.5, md: 3 },
        borderRadius: 4,
        bgcolor: "background.paper",
        border: `1px solid rgba(255, 255, 255, 0.05)`,
        display: "flex",
        flexDirection: "column",
        height: "100%",
        width: "100%",
      }}
    >
      <Typography
        component="h2"
        variant="subtitle1"
        fontWeight={800}
        mb={2.5}
        sx={{ display: "flex", alignItems: "center", gap: 1 }}
      >
        <Box
          component="span"
          sx={{
            width: 4,
            height: 20,
            bgcolor: "secondary.main",
            borderRadius: 2,
          }}
        />
        {icon}
        {title}
      </Typography>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          width: "100%",
        }}
      >
        {children}
      </Box>
    </Paper>
  );

  return (
    <Box
      component="main"
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: { xs: 2.5, md: 3 },
        width: "100%",
      }}
    >
      {/* About Section */}
      <SectionCard
        title="درباره باشگاه"
        icon={<Info sx={{ color: "secondary.main", fontSize: 22 }} />}
      >
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ lineHeight: 2, whiteSpace: "pre-line" }}
        >
          {gymData?.description || "توضیحاتی ثبت نشده است."}
          {gymData?.about && `\n\n${gymData.about}`}
        </Typography>
      </SectionCard>
      {/* Minimal Gallery Section */}
      {getGalleryImages().length > 0 && (
        <SectionCard
          title="گالری تصاویر"
          icon={<Typography fontSize="1.2rem">📸</Typography>}
        >
          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
            {getGalleryImages().map((item, idx) => (
              <Box
                key={idx}
                component="img"
                src={import.meta.env.VITE_URL + item}
                alt={`تصویر گالری ${idx + 1}`}
                onClick={() => handleOpenLightbox(idx)}
                sx={{
                  width: { xs: 70, sm: 80, md: 90 },
                  height: "auto",
                  aspectRatio: "1/1",
                  objectFit: "cover",
                  borderRadius: 2,
                  cursor: "pointer",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                  transition: "transform 0.2s ease, box-shadow 0.2s ease",
                  "&:hover": {
                    transform: "scale(1.05)",
                    boxShadow: `0 4px 12px ${theme.palette.secondary.main}60`,
                  },
                }}
              />
            ))}
          </Box>
        </SectionCard>
      )}
      {/* Location & Contact Grid */}
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <SectionCard
            title="موقعیت مکانی"
            icon={<LocationOn sx={{ color: "primary.main", fontSize: 22 }} />}
          >
            <Stack spacing={2}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  آدرس دقیق
                </Typography>
                <Typography variant="body2" fontWeight={600} mt={0.5}>
                  {getFullAddress()}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  منطقه
                </Typography>
                <Typography variant="body2" fontWeight={600} mt={0.5}>
                  {getLocationString()}
                </Typography>
              </Box>
            </Stack>
          </SectionCard>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <SectionCard
            title="ارتباط با ما"
            icon={<Phone sx={{ color: "primary.main", fontSize: 22 }} />}
          >
            <Stack spacing={2}>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="caption" color="text.secondary">
                  <AccessTime
                    sx={{ fontSize: 14, verticalAlign: "middle", mr: 0.5 }}
                  />{" "}
                  ساعت کاری
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {getWorkingHours()}
                </Typography>
              </Box>
              <Divider sx={{ borderColor: "rgba(255,255,255,0.05)" }} />
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="caption" color="text.secondary">
                  تلفن تماس
                </Typography>
                <Typography variant="body2" fontWeight={600} dir="ltr">
                  {gymData?.phone || "---"}
                </Typography>
              </Box>
              {gymData?.website && (
                <>
                  <Divider sx={{ borderColor: "rgba(255,255,255,0.05)" }} />
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      وبسایت
                    </Typography>
                    <Typography
                      variant="body2"
                      color="secondary.main"
                      component="a"
                      href={gymData.website}
                      target="_blank"
                      sx={{ textDecoration: "none" }}
                    >
                      مشاهده سایت
                    </Typography>
                  </Box>
                </>
              )}
            </Stack>
          </SectionCard>
        </Grid>
      </Grid>

      {/* Sports & Amenities Grid */}
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper
            component="section"
            sx={{
              p: 2,
              height: "100%",
              border: `1px solid ${theme.palette.primary.dark}90`,
              bgcolor: "rgba(92, 107, 192, 0.03)",
            }}
          >
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}
            >
              <FitnessCenter color="primary" fontSize="small" />
              <Typography component="h2" variant="subtitle2" fontWeight={800}>
                رشته های ورزشی
              </Typography>
            </Box>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {getSportNames().map((item, idx) => (
                <Chip
                  key={idx}
                  label={item?.name || item}
                  size="small"
                  sx={{
                    bgcolor: "primary.main",
                    color: "#fff",
                    fontWeight: 600,
                    borderRadius: 2,
                  }}
                />
              ))}
            </Box>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Paper
            sx={{
              p: 3,
              height: "100%",
              border: `1px solid ${theme.palette.primary.dark}90`,
              bgcolor: "rgba(92, 107, 192, 0.03)",
            }}
            icon={
              <Star sx={{ color: theme.palette.warning.main, fontSize: 22 }} />
            }
          >
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}
            >
              <LocationOn color="secondary" fontSize="small" />
              <Typography component="h2" variant="subtitle2" fontWeight={800}>
                امکانات رفاهی
              </Typography>
            </Box>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {getAmenities()?.length > 0 ? (
                getAmenities().map((item, idx) => (
                  <Chip
                    key={idx}
                    label={item?.name || item}
                    size="small"
                    variant="outlined"
                    color="secondary"
                    sx={{ fontWeight: 600, borderRadius: 2, p: 1 }}
                  />
                ))
              ) : (
                <Typography variant="caption" color="text.secondary">
                  نامشخص
                </Typography>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Achievements Section */}
      {getAchievements().length > 0 && (
        <SectionCard
          title="افتخارات و مدارک"
          icon={
            <EmojiEvents
              sx={{ color: theme.palette.warning.main, fontSize: 22 }}
            />
          }
        >
          <Grid container spacing={2}>
            {getAchievements().map((ach, idx) => (
              <Grid size={{ xs: 12, md: 6 }} key={idx}>
                <Paper
                  sx={{
                    p: 2,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    bgcolor: "rgba(255,255,255,0.02)",
                    borderRadius: 3,
                    borderLeft: `3px solid ${theme.palette.success.main}`,
                  }}
                >
                  <Typography variant="body2" fontWeight={600}>
                    {ach?.title}
                  </Typography>
                  {ach?.year && (
                    <Chip
                      label={ach.year}
                      size="small"
                      variant="outlined"
                      sx={{ height: 20, fontSize: "0.6rem" }}
                    />
                  )}
                </Paper>
              </Grid>
            ))}
          </Grid>
        </SectionCard>
      )}

      {/* Pricing Section */}
      {gymData?.pricing?.length > 0 && (
        <SectionCard
          title="تعرفه‌های خدمات"
          icon={<Verified sx={{ color: "primary.main", fontSize: 22 }} />}
          sx={{
            background:
              "linear-gradient(145deg, rgba(30,30,30,1) 0%, rgba(40,45,60,1) 100%)",
            borderColor: `${theme.palette.primary.main}50`,
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {gymData.pricing.map((item, idx) => (
              <Box
                key={idx}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  p: 1.5,
                  bgcolor: "rgba(0,0,0,0.3)",
                  borderRadius: 2,
                  border: "1px solid rgba(255,255,255,0.05)",
                }}
              >
                <Typography variant="body2" fontWeight={700}>
                  {item?.title}
                </Typography>
                <Typography
                  fontWeight={800}
                  color="primary.light"
                  sx={{ fontSize: "0.95rem" }}
                >
                  {item?.price?.toLocaleString()}{" "}
                  <Typography
                    component="span"
                    variant="caption"
                    color="text.secondary"
                  >
                    تومان
                  </Typography>
                </Typography>
              </Box>
            ))}
          </Box>
        </SectionCard>
      )}

      {/* Comments */}
      <Box component="section">
        <CommentsSection targetId={gymId} targetType="Gym" />
      </Box>

      {/* Swiper Lightbox Modal */}
      <Dialog
        open={lightboxOpen}
        onClose={handleCloseLightbox}
        maxWidth="lg"
        fullWidth
        keepMounted
        sx={{ zIndex: "100000000" }}
        PaperProps={{
          sx: {
            bgcolor: "rgba(0, 0, 0, 0.44)",
            boxShadow: "none",
            overflow: "hidden",
            height: "100%",
            m: { xs: 0, md: 4 },
            borderRadius: { xs: 0, md: 4 },
          },
        }}
      >
        <Box sx={{ width: "100%", height: "100%", position: "relative" }}>
          <IconButton
            onClick={handleCloseLightbox}
            sx={{
              position: "absolute",
              top: { xs: 16, md: 24 },
              right: { xs: 16, md: 24 },
              color: "#fff",
              bgcolor: "rgba(255,255,255,0.1)",
              zIndex: 50,
              "&:hover": { bgcolor: "rgba(255,255,255,0.2)" },
            }}
          >
            <Close />
          </IconButton>

          {lightboxOpen && (
            <Swiper
              modules={[Navigation, Pagination, Zoom]}
              navigation
              pagination={{ clickable: true, dynamicBullets: true }}
              zoom={true}
              initialSlide={initialSlide}
              spaceBetween={30}
              slidesPerView={1}
              style={{
                width: "100%",
                height: "100%",
                "--swiper-navigation-color": theme.palette.secondary.main,
                "--swiper-pagination-color": theme.palette.secondary.main,
              }}
            >
              {getGalleryImages().map((img, idx) => (
                <SwiperSlide
                  key={idx}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <div className="swiper-zoom-container">
                    <img
                      src={import.meta.env.VITE_URL + img}
                      alt={`گالری تصویر بزرگ ${idx + 1}`}
                      style={{
                        maxWidth: "100%",
                        maxHeight: "85vh",
                        objectFit: "contain",
                        borderRadius: "8px",
                      }}
                    />
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          )}
        </Box>
      </Dialog>
    </Box>
  );
}
